import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import BreadcrumbsModulePage from "../breadcrumbs/BreadcrumbsModulePage";
import DownloadPdfButton from "../download_pdf_button/DownloadPdfButton";
import { modulesData } from "./modulesData";
import { getModulesByAuthor } from "../../api/subjects";
import "../class_page/class_page.css";

const normalize = (v) => String(v ?? "").trim();
const lower = (v) => normalize(v).toLowerCase();

const getLegacyModulesSafe = (data, id, subject, author) => {
  if (!data) return [];

  const idStr = normalize(id);
  const idNumStr = String(Number(idStr));
  const subj = normalize(subject);
  const auth = normalize(author);

  const direct = data?.[idStr]?.[subj]?.[auth] || data?.[idNumStr]?.[subj]?.[auth] || [];

  if (Array.isArray(direct) && direct.length) return direct;

  const classKeys = Object.keys(data || {});
  const classKey =
    classKeys.find((k) => normalize(k) === idStr) ||
    classKeys.find((k) => normalize(k) === idNumStr) ||
    classKeys.find((k) => lower(k) === lower(idStr)) ||
    classKeys.find((k) => lower(k) === lower(idNumStr));

  if (!classKey) return [];

  const subjObj = data[classKey] || {};
  const subjKeys = Object.keys(subjObj || {});
  const subjKey =
    subjKeys.find((k) => normalize(k) === subj) ||
    subjKeys.find((k) => lower(k) === lower(subj));

  if (!subjKey) return [];

  const authObj = subjObj[subjKey] || {};
  const authKeys = Object.keys(authObj || {});
  const authKey =
    authKeys.find((k) => normalize(k) === auth) ||
    authKeys.find((k) => lower(k) === lower(auth));

  if (!authKey) return [];

  const arr = authObj[authKey];
  return Array.isArray(arr) ? arr : [];
};

const normalizeSlug = (module) => {
  const slug = module?.moduleSlug ?? module?.path ?? "";
  return normalize(slug);
};

const mergeModules = (legacy, mongo) => {
  const map = new Map();

  (Array.isArray(legacy) ? legacy : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title),
      order: typeof module?.order === "number" ? module.order : idx,
      enabled: true,
      pdfPath: module?.pdfPath || null,
      pdfFileName: module?.pdfFileName || null,
      __source: "legacy",
    });
  });

  (Array.isArray(mongo) ? mongo : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;

    const prev = map.get(slug);
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title ?? prev?.title),
      order:
        typeof module?.order === "number"
          ? module.order
          : typeof prev?.order === "number"
            ? prev.order
            : idx,
      enabled:
        typeof module?.enabled === "boolean"
          ? module.enabled
          : typeof prev?.enabled === "boolean"
            ? prev.enabled
            : true,
      pdfPath: module?.pdfPath ?? prev?.pdfPath ?? null,
      pdfFileName: module?.pdfFileName ?? prev?.pdfFileName ?? null,
      __source: prev ? "merged" : "mongo",
    });
  });

  return Array.from(map.values())
    .filter((module) => module.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const AuthorPage = ({ userName, userRoles }) => {
  const { id, subject, author } = useParams();
  const navigate = useNavigate();

  const [mongoModules, setMongoModules] = useState([]);
  const [loading, setLoading] = useState(false);

  const legacyModules = useMemo(() => {
    return getLegacyModulesSafe(modulesData, id, subject, author);
  }, [id, subject, author]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id || !subject || !author) return;
      setLoading(true);
      try {
        const data = await getModulesByAuthor(id, subject, author);
        if (mounted) setMongoModules(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setMongoModules([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id, subject, author]);

  const modules = useMemo(() => {
    return mergeModules(legacyModules, mongoModules);
  }, [legacyModules, mongoModules]);

  return (
    <>
      <Header userName={userName} userRoles={userRoles} />
      <BreadcrumbsModulePage />

      <Container maxWidth="md" className="catalog-page">
        <Box className="catalog-page__heading">
          <Typography variant="h4" component="h1">
            Модулі
          </Typography>
          <Typography color="text.secondary">
            Оберіть модуль для проходження або завантажте PDF, якщо він доступний.
          </Typography>
        </Box>

        <Paper className="catalog-panel" variant="outlined">
          {modules.length > 0 ? (
            <List disablePadding className="catalog-list">
              {modules.map((module) => (
                <Box key={module.moduleSlug} className="module-list-row">
                  <ListItemButton
                    className="catalog-list__item"
                    onClick={() =>
                      navigate(`/class/${id}/${subject}/${author}/modules/${module.moduleSlug}`)
                    }
                  >
                    <ListItemText primary={module.title} />
                    <ChevronRightRoundedIcon />
                  </ListItemButton>

                  {module.pdfPath && (
                    <DownloadPdfButton
                      pdfPath={module.pdfPath}
                      pdfFileName={module.pdfFileName}
                    />
                  )}
                </Box>
              ))}
            </List>
          ) : (
            <Box className="catalog-state">
              {loading && <CircularProgress size={28} />}
              <Typography>
                {loading ? "Завантаження..." : "Модулі не знайдені"}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default AuthorPage;
